<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('document_request_title')" :isFilter="false"></Header>
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('document_request_title')" :isFilter="false"></HeaderMobile>
            </template>
            <div class="row data-form">
                <div class="col-12">
                    <ValidationObserver ref="documentForm">
                        <div class="mb-2">
                            <h6 class="mb-0">{{ $t('student_information') | toUpperCase }}</h6>
                        </div>
                        <div class="border px-5 pt-5 pb-2 mb-4 rounded">
                            <div class="row">
                                <div class="col-12 col-sm-6 col-xl-3">
                                    <b-form-group :label="$t('student_number')">
                                        {{ formData.student_number }}
                                    </b-form-group>
                                </div>
                                <div class="col-12 col-sm-6 col-xl-3">
                                    <b-form-group :label="$t('name_surname')">
                                        {{ formData.name + ' ' + formData.surname }}
                                    </b-form-group>
                                </div>
                                <div class="col-12 col-sm-6 col-xl-3">
                                  <b-form-group :label="$t('program')">
                                    {{ formData.program_name }}
                                  </b-form-group>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-12 col-sm-6 col-xl-3">
                                    <b-form-group :label="$t('email')">
                                        {{ formData.email }}
                                    </b-form-group>
                                </div>
                                <div class="col-12 col-sm-6 col-xl-3">
                                    <b-form-group :label="$t('mobile_number')">
                                        {{ formData.mobile_tel }}
                                    </b-form-group>
                                </div>
                                <div class="col-12 col-sm-6 col-xl-3">
                                    <b-form-group :label="$t('address')">
                                        {{ formData.address }}
                                    </b-form-group>
                                </div>
                            </div>

                            <!-- Document Type -->
                            <div class="row mt-4">
                                <div class="col-12 col-sm-4 col-md-4 col-lg-4">
                                    <ValidationProvider name="document_type" rules="required" v-slot="{valid, errors}">
                                        <b-form-group :label="$t('document_type')">
                                            <document-request-type-selectbox
                                                @returnOptions="returnOptions"
                                                :validate-error="errors[0]"
                                                :is-tefl="isTeflStudent"
                                                v-model="documentForm.document_type">
                                            </document-request-type-selectbox>
                                        </b-form-group>
                                    </ValidationProvider>
                                </div>
                            </div>

                            <!-- Extra fields for mentora_transcript / mentora_certificate -->
                            <div v-if="isMentoraType" class="mt-3">
                                <hr/>
                                <div class="mb-2">
                                    <h6 class="mb-0">{{ $t('request_details') | toUpperCase }}</h6>
                                </div>

                                <!-- Requested Count -->
                                <div class="row">
                                    <div class="col-12 col-sm-4 col-md-3">
                                        <ValidationProvider name="requested_count" rules="required" v-slot="{valid, errors}">
                                            <b-form-group :label="$t('requested_count')">
                                                <b-form-input
                                                    type="number"
                                                    v-model.number="documentForm.requested_count"
                                                    :min="1"
                                                    :max="20"
                                                    :state="errors[0] ? false : null"
                                                ></b-form-input>
                                                <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </div>
                                </div>

                                <!-- Delivery Type -->
                                <div class="row">
                                    <div class="col-12 col-sm-4 col-md-3">
                                        <ValidationProvider name="delivery_type" rules="required" v-slot="{valid, errors}">
                                            <b-form-group :label="$t('delivery_type')">
                                                <b-form-select
                                                    v-model="documentForm.delivery_type"
                                                    :options="deliveryTypeOptions"
                                                    :state="errors[0] ? false : null"
                                                ></b-form-select>
                                                <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                            </b-form-group>
                                        </ValidationProvider>
                                    </div>
                                </div>

                                <!-- Pickup Fields -->
                                <div v-if="documentForm.delivery_type === 'pickup'">
                                    <div class="row">
                                        <div class="col-12 col-sm-4 col-md-3">
                                            <ValidationProvider name="pickup_type" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('pickup_type')">
                                                    <b-form-select
                                                        v-model="documentForm.pickup_type"
                                                        :options="pickupTypeOptions"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-select>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>
                                    </div>

                                    <!-- Pickup Person Name (only when pickup_type = other) -->
                                    <div class="row" v-if="documentForm.pickup_type === 'other'">
                                        <div class="col-12 col-sm-6 col-md-4">
                                            <ValidationProvider name="pickup_person_name" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('pickup_person_name')">
                                                    <b-form-input
                                                        v-model="documentForm.pickup_person_name"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>
                                    </div>
                                </div>

                                <!-- FedEx Shipping Fields -->
                                <div v-if="documentForm.delivery_type === 'fedex'">
                                    <div class="row">
                                        <!-- Shipping Type -->
                                        <div class="col-12 col-sm-6 col-md-5">
                                            <ValidationProvider name="shipping_type" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('shipping_type')">
                                                    <b-form-radio
                                                        v-model="documentForm.shipping_type"
                                                        value="domestic"
                                                        :class="errors[0] ? 'is-invalid' : ''"
                                                    >
                                                        {{ $t('domestic') }} <strong>$85</strong>
                                                    </b-form-radio>
                                                    <small class="text-muted d-block ml-4 mt-1 mb-2">
                                                        <em>{{ $t('domestic_shipping_note') }}</em>
                                                    </small>
                                                    <b-form-radio
                                                        v-model="documentForm.shipping_type"
                                                        value="international"
                                                        :class="errors[0] ? 'is-invalid' : ''"
                                                    >
                                                        {{ $t('international') }} <strong>$130</strong>
                                                    </b-form-radio>
                                                    <b-form-invalid-feedback v-if="errors[0]" class="d-block">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <!-- Recipient Full Name -->
                                        <div class="col-12 col-sm-6 col-md-4">
                                            <ValidationProvider name="recipient_full_name" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('recipient_full_name')">
                                                    <b-form-input
                                                        v-model="documentForm.recipient_full_name"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <!-- Delivery Phone -->
                                        <div class="col-12 col-sm-6 col-md-4">
                                            <ValidationProvider name="delivery_phone" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('delivery_phone')">
                                                    <b-form-input
                                                        type="tel"
                                                        v-model="documentForm.delivery_phone"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <!-- Street Address -->
                                        <div class="col-12 col-sm-6 col-md-4">
                                            <ValidationProvider name="street_address" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('street_address')">
                                                    <b-form-input
                                                        v-model="documentForm.street_address"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <!-- Apartment / Suite (optional) -->
                                        <div class="col-12 col-sm-6 col-md-4">
                                            <b-form-group :label="$t('apartment_suite')">
                                                <b-form-input
                                                    v-model="documentForm.apartment_suite"
                                                ></b-form-input>
                                            </b-form-group>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <!-- City -->
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <ValidationProvider name="delivery_city" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('delivery_city')">
                                                    <b-form-input
                                                        v-model="documentForm.delivery_city"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <!-- State / Province / Region -->
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <ValidationProvider name="state_province_region" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('state_province_region')">
                                                    <b-form-input
                                                        v-model="documentForm.state_province_region"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <!-- Zip / Postal Code -->
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <ValidationProvider name="zip_postal_code" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('zip_postal_code')">
                                                    <b-form-input
                                                        v-model="documentForm.zip_postal_code"
                                                        :state="errors[0] ? false : null"
                                                    ></b-form-input>
                                                    <b-form-invalid-feedback v-if="errors[0]">{{ errors[0] }}</b-form-invalid-feedback>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>

                                        <!-- Country -->
                                        <div class="col-12 col-sm-6 col-md-3">
                                            <ValidationProvider name="delivery_country" rules="required" v-slot="{valid, errors}">
                                                <b-form-group :label="$t('delivery_country')">
                                                    <country-selectbox
                                                        v-model="documentForm.delivery_country"
                                                        :validate-error="errors[0]"
                                                    ></country-selectbox>
                                                </b-form-group>
                                            </ValidationProvider>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </ValidationObserver>
                </div>
                <div class="col-12 col-sm-12 col-md-12 col-lg-12 d-flex align-items-center">
                    <b-button variant="primary" v-if="!showMakePaymentButton" @click="sendForm">{{$t('save')}}</b-button>
                    <b-button variant="success" v-if="showMakePaymentButton === 'waiting_payment'" class="ml-2" @click="onlinePayment">{{ $t('make_payment') }}</b-button>
                </div>
            </div>
        </app-layout>

    </div>
</template>

<script>
    // Components
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';
    import DocumentRequestTypeSelectbox from '@/components/interactive-fields/DocumentRequestTypeSelectbox';
    import CountrySelectbox from '@/components/interactive-fields/CountrySelectbox';

    // Services
    import DocumentRequestService from '@/services/DocumentRequestService';
    import PersonService from '@/services/PersonService';
    import ConstantService from '@/services/ConstantService';

    // Other
    import {ValidationObserver, ValidationProvider} from 'vee-validate'
    import CustomerService from "@/services/CustomerService";
    import Base from "@/plugins/Base";
    // File


    export default {
    components: {
        AppLayout,
        Header,
        HeaderMobile,
        DocumentRequestTypeSelectbox,
        CountrySelectbox,

        ValidationProvider,
        ValidationObserver,
    },
    data() {
        return {
            formData: {},
            documentForm: {
                document_type: null,
                requested_count: 1,
                delivery_type: null,
                pickup_type: null,
                pickup_person_name: null,
                shipping_type: null,
                recipient_full_name: null,
                street_address: null,
                apartment_suite: null,
                delivery_city: null,
                state_province_region: null,
                zip_postal_code: null,
                delivery_country: null,
                delivery_phone: null,
            },
            price: null,
            typeOptions: [],
            deliveryDisabled: false,
            countDisabled: false,

            deliveryTypeOptions: [
                { value: null, text: this.$t('select') },
                { value: 'pickup', text: this.$t('pickup') },
                { value: 'fedex', text: this.$t('fedex') },
            ],
            pickupTypeOptions: [
                { value: null, text: this.$t('select') },
                { value: 'self', text: this.$t('self') },
                { value: 'other', text: this.$t('other') },
            ],

            gsmVerifyForm: {
                gsmNo: null
            },

            smsSendButtonStatus: true,
            smsSendAgainButtonStatus: false,
            alertVariant: "success",
            alertShow: true,
            forgotPass: false,
            timerCount: 5,

            smsVerifyActionForm: {
                verifyCode: null
            },

            addressForm: {
                state: null,
                city: null,
                address: null
            },
            showMakePaymentButton: null,
        }
    },
    computed: {
        isTeflStudent() {
            const user = this.$store.getters['auth/getUser'];
            return user?.active_student_program?.program_code === '0702006';
        },
        isMentoraType() {
            return this.documentForm.document_type === 'mentora_transcript'
                || this.documentForm.document_type === 'mentora_certificate';
        },
        calcPrice: function (){
            var result = ''
            if(this.documentForm.document_type != null && this.documentForm.document_type != ''){
                this.typeOptions.forEach(item => {
                    if(item.value == this.documentForm.document_type){
                        let priceObj = item.data.split(':')
                        let price = priceObj[1].replace('}','')
                        result = price == 0 ? this.$t('priceless') +' 0TL' : price+' TL'
                    }
                })
                return result
            } else {
                return '-'
            }
        }
    },
    methods: {
        getData() {
            DocumentRequestService.studentForm()
                                  .then(response => {
                                      this.formData = response.data.data
                                      this.gsmVerifyForm.gsmNo = response.data.data.mobile_tel
                                  })
        },

        async sendForm(){
            const isValid = await this.$refs.documentForm.validate();
            if (isValid) {
                let formData = {
                    document_type: this.documentForm.document_type,
                }

                // Append extra fields for mentora types
                if (this.isMentoraType) {
                    formData.requested_count = this.documentForm.requested_count;
                    formData.delivery_type = this.documentForm.delivery_type;

                    if (this.documentForm.delivery_type === 'pickup') {
                        formData.pickup_type = this.documentForm.pickup_type;
                        if (this.documentForm.pickup_type === 'other') {
                            formData.pickup_person_name = this.documentForm.pickup_person_name;
                        }
                    }

                    if (this.documentForm.delivery_type === 'fedex') {
                        formData.shipping_type = this.documentForm.shipping_type;
                        formData.recipient_full_name = this.documentForm.recipient_full_name;
                        formData.street_address = this.documentForm.street_address;
                        formData.delivery_city = this.documentForm.delivery_city;
                        formData.state_province_region = this.documentForm.state_province_region;
                        formData.zip_postal_code = this.documentForm.zip_postal_code;
                        formData.delivery_country = this.documentForm.delivery_country;
                        formData.delivery_phone = this.documentForm.delivery_phone;

                        if (this.documentForm.apartment_suite) {
                            formData.apartment_suite = this.documentForm.apartment_suite;
                        }
                    }
                }

                DocumentRequestService.store(formData).then(response => {
                    this.$toast.success(this.$t('api.' + response.data.message));

                    const status = response?.data?.data?.status;
                    this.showMakePaymentButton = status;

                    if (status !== 'waiting_payment') {
                        this.$router.push('/my-mentora-requests/4')
                    }
                }).catch(e => {
                    if (e.status == '406') {
                        if (e.data.message) {
                            this.$toast.error(this.$t('api.' + e.data.message));
                        }
                    }
                    else if (e.status == '422') {
                        if (e.data.errors) {
                            const formRef = this.$refs.documentForm;
                            for (const [key, value] of Object.entries(e.data.errors)) {
                                if (formRef && formRef.errors && formRef.errors[key]) {
                                    formRef.errors[key].push(value[0]);
                                }
                            }
                        }
                        if (e.data.message) {
                            this.$toast.error(this.$t('api.' + e.data.message));
                        }
                    }
                })
            }
        },

        returnOptions(options){
            this.typeOptions = options;
        },

        openModalClick() {
            this.$refs.statementModal.$refs.commonModal.show()
        },

        checkStatement2() {
            this.documentForm.accept = true
            this.$refs.statementModal.$refs.commonModal.hide()
        },

        openSmsVerify() {
            this.$refs.gsmVerifyModal.$refs.commonModal.show()
        },

        async infoUpdate() {
            const isValid = await this.$refs.mobileForm.validate();
            if (isValid) {
                let gsmData = {
                    mobile_tel: this.gsmVerifyForm.gsmNo
                }

                PersonService.sendSmsCode(gsmData).then(response => {
                    let data = response.data.data;
                    this.forgotPass = true
                    this.timerCount = data.expires_in
                    this.timerEnabled = true
                    this.smsSendButtonStatus = false
                    this.smsSendAgainButtonStatus = false
                }).catch(e => {
                    if (e.status == '404') { ///406 geldiği zaman değiştirilecek
                        if (e.data.message) {
                            this.$refs.mobileForm.errors.gsmNo.push(e.data.message);
                        }
                    }
                    else {
                        if (e.data.errors.phone) {
                            this.$refs.mobileForm.errors.gsmNo.push(e.data.errors.phone[0]);
                        }
                        if (e.data.errors.token) {
                            this.$refs.mobileForm.errors.gsmNo.push(e.data.errors.token[0]);
                        }
                    }
                })
            }

        },

        AddressUpdateClick() {
            this.$refs.addressModal.$refs.commonModal.show()
            this.$refs.gsmVerifyModal.$refs.commonModal.hide()
        },

        async gsmVerify() {

            const isValid = await this.$refs.smsVerifyActionForm.validate();
            if (isValid) {
                let smsData = {
                    sms_code: this.smsVerifyActionForm.verifyCode
                }
                PersonService.verifySmsCode(smsData)
                             .then(response => {
                                 let data = response.data;
                                 if (data.success == false) {
                                     this.$refs.smsVerifyActionForm.errors.smsVerifyCode.push(data.message);
                                 }
                                 else {
                                     this.$refs.gsmVerifyModal.$refs.commonModal.hide()
                                     this.$toast.success(this.$t('api.' + data.message));
                                     this.$refs.emailVerifyModal.$refs.commonModal.show()
                                     this.getData()
                                 }
                             })
            }

        },

        EnterCodeAgainFunc() {
            this.alertVariant = "success"
            this.infoUpdate()
        },

        async addressUpdate() {
            const isValid = await this.$refs.addressForm.validate();
            if (isValid) {
                let addressData = {
                    contact_address: this.addressForm.address,
                    contact_state_id: this.addressForm.state,
                    contact_city_id: this.addressForm.city
                }

                PersonService.patchAddress(addressData)
                             .then(response => {
                                 let data = response.data
                                 this.$toast.success(this.$t('api.' + data.message));
                                 this.$refs.addressModal.$refs.commonModal.hide()
                                 this.getData()
                             })
                             .catch(e => {
                                 if (e.status == '404') { ///406 geldiği zaman değiştirilecek
                                     if (e.data.message) {
                                         this.$refs.addressForm.errors.city.push(e.data.message);
                                     }
                                 }
                                 else {
                                     if (e.data.errors.contact_address) {
                                         this.$refs.addressForm.errors.address.push(e.data.errors.contact_address[0]);
                                     }
                                     if (e.data.errors.contact_state_id) {
                                         this.$refs.addressForm.errors.state.push(e.data.errors.contact_state_id[0]);
                                     }
                                     if (e.data.errors.contact_city_id) {
                                         this.$refs.addressForm.errors.city.push(e.data.errors.contact_city_id[0]);
                                     }
                                 }
                             })
            }
        },
        onlinePayment() {
            let data = {student_program_id: this.formData.student_program_id};
            CustomerService.createEpaymentLogin(data)
                .then(response => {
                    let data = response.data.data;
                    Base.LocalStorage.set('epayment_token', data.token);
                    this.setWithExpiry('epayment_redirect_after_payment', window.location.pathname);
                    this.$store.commit('epayment/setEpaymentToken', 'Bearer ' + data.token);
                    Base.LocalStorage.set('epayment_user', data.customer);
                    this.$store.commit('epayment/setEpaymentUser', data.customer)
                    this.$router.push('/epayment');
                })
                .catch(e => {
                    this.showErrors(e);
                })
        },

        resetDeliveryFields() {
            this.documentForm.pickup_type = null;
            this.documentForm.pickup_person_name = null;
            this.documentForm.shipping_type = null;
            this.documentForm.recipient_full_name = null;
            this.documentForm.street_address = null;
            this.documentForm.apartment_suite = null;
            this.documentForm.delivery_city = null;
            this.documentForm.state_province_region = null;
            this.documentForm.zip_postal_code = null;
            this.documentForm.delivery_country = null;
            this.documentForm.delivery_phone = null;
        },

    },

    watch: {
        'documentForm.document_type': {
            handler(value) {
                // Reset extra fields when document type changes
                this.documentForm.delivery_type = null;
                this.resetDeliveryFields();
                this.documentForm.requested_count = 1;
            }
        },

        'documentForm.delivery_type': {
            handler(value) {
                // Reset delivery-specific fields when delivery type changes
                this.resetDeliveryFields();
            }
        },

        'documentForm.pickup_type': {
            handler(value) {
                if (value !== 'other') {
                    this.documentForm.pickup_person_name = null;
                }
            }
        },

        timerEnabled(value) {
            this.timerCount--;
        },
        timerCount: {
            handler(value) {

                if (value > 0 && this.timerEnabled) {
                    setTimeout(() => {
                        this.timerCount--;
                    }, 1000);
                }
                else if (value > 0 && !this.timerEnabled) {
                    this.alertShow = false
                    this.smsSendAgainButtonStatus = false
                }
                else {
                    this.alertVariant = "danger"
                    this.alertShow = true
                    this.smsSendAgainButtonStatus = true
                }

            },
            immediate: true,
        },

        timerEnabledEmail(value) {
            this.timerCountEmail--;
        },
        timerCountEmail: {
            handler(value) {

                if (value > 0 && this.timerEnabledEmail) {
                    setTimeout(() => {
                        this.timerCountEmail--;
                    }, 1000);
                }
                else if (value > 0 && !this.timerEnabledEmail) {
                    this.alertShowEmail = false
                    this.smsSendAgainButtonStatus = false
                }
                else {
                    this.alertVariantEmail = "danger"
                    this.alertShowEmail = true
                    this.emailSendAgainButtonStatus = true
                }

            },
            immediate: true,
        }
    },

    created() {
        this.getData();

    }
}
</script>

<style>

.auto-as-parent{
    height: 100%;
    width: 100%;
}

</style>
